import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { T, F, th as palette, I, Over, Pill, Btn, Img } from "@/lib/bricks";
import { toast } from "sonner";
import VisitViewer from "@/components/VisitViewer";
import VisitEmbedPanel from "@/components/visit/VisitEmbedPanel";
import { VISIT_ACCEPT } from "@/lib/visitManifest";
import { visitKindLabel } from "@/lib/visitBridge";
import { deliverableTab } from "@/lib/mediaKind";
import { AMENITY_PRESETS } from "@/constants/amenityPresets";
import { formatShootWhen } from "@/lib/shoot-window";
import { StatusTabs, SmallSelect, Section, List, Row, Empty, Loading, StatusText, humanize } from "@/components/calm/Calm";
import { useListingMedia } from "@/lib/listingMedia";
import { BookMediaButton, ListingMediaPrompt } from "@/components/listing/BookMedia";

const MEDIA_TABS = ['All', 'Photos', 'Drone', 'Reel', 'Plan', 'Virtual Visit'];
const TAB_CSS = '.pd-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}@media (min-width:640px){.pd-grid{grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}}.pd-tabsel{display:block}.pd-tabs{display:none}@media (min-width:640px){.pd-tabsel{display:none}.pd-tabs{display:block}}';

const EMPTY_CAPTURES = {
  purchased: { tour: false, floorplan: false, visit: false },
  splat: { purchased: false, empty: true, session: null, display_status: null, next: null, embed_url: null, tags: [], session_id: null, job_id: null },
  floorplan: { purchased: false, empty: true, session: null, display_status: null, next: null, output_image_url: null },
};

function pillTone(label) {
  if (label === 'delivered') return 'ok';
  if (label === 'in_progress' || label === 'in progress') return 'warn';
  if (label === 'submitted') return 'brick';
  return 'neutral';
}

function capturePills(block) {
  if (!block || !block.purchased) return [];
  const pills = [];
  const st = block.display_status;
  if (st) pills.push(st.replace(/_/g, ' '));
  if (block.next === 'splatting_todo') pills.push('splatting todo');
  if (block.next === 'tracing_todo') pills.push('tracing todo');
  return pills;
}

function tourEmbedSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
}

function CaptureCard({ title, emptyLabel, block, dark, p, children }) {
  const pills = capturePills(block);
  const empty = !pills.length;
  const frames = block?.session?.frame_count;
  return (
    <div style={{ flex: 1, minWidth: 0, padding: 16, border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf }}>
      <Over color={p.ink3}>{title}</Over>
      {empty ? (
        <div style={{ marginTop: 10, fontSize: 13, color: p.ink3 }}>{emptyLabel}</div>
      ) : (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {pills.map(label => (
            <Pill key={label} tone={pillTone(label)} dark={dark}>{label}</Pill>
          ))}
        </div>
      )}
      {!empty && frames != null && frames > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: p.ink4, fontFamily: F.mono }}>
          {frames} frame{frames === 1 ? '' : 's'}
        </div>
      )}
      {children}
    </div>
  );
}


function FeatureTagsPanel({ propertyId, block, dark, p, onChanged }) {
  const sessionId = block?.session_id || block?.session?.id || null;
  const delivered = block?.display_status === 'delivered' && !!tourEmbedSrc(block?.embed_url);
  const frames = Math.max(1, Number(block?.session?.frame_count) || 1);
  const stationOptions = Array.from({ length: frames }, (_, i) => i);
  const [tags, setTags] = useState(() => Array.isArray(block?.tags) ? block.tags : (Array.isArray(block?.session?.tags) ? block.session.tags : []));
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [stationIndex, setStationIndex] = useState(0);
  const [yaw, setYaw] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const next = Array.isArray(block?.tags) ? block.tags : (Array.isArray(block?.session?.tags) ? block.session.tags : []);
    setTags(next);
  }, [block?.tags, block?.session?.tags, block?.session_id, block?.embed_url]);

  if (!delivered || !sessionId) return null;

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13, fontFamily: F.display,
    border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf2,
    color: p.ink, outline: 'none', boxSizing: 'border-box',
  };

  async function handleAdd() {
    const lab = label.trim();
    if (!lab) {
      setMsg('Tag needs a label — e.g. Walk-in closet. Empty amenities are not invented.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post(`/properties/${propertyId}/captures/tags`, {
        session_id: sessionId,
        label: lab,
        note: note.trim() || undefined,
        station_index: Number(stationIndex) || 0,
        yaw: Number(yaw) || 0,
        pitch: Number(pitch) || 0,
      });
      const next = Array.isArray(res.data?.tags) ? res.data.tags : null;
      if (next) setTags(next);
      setLabel('');
      setNote('');
      setMsg(res.data?.rebuilt === false
        ? 'Tag saved. Hard-refresh the tour iframe if markers do not appear.'
        : 'Tag on the tour. Hard-refresh the listing iframe to see it.');
      toast.success('Feature tag added');
      onChanged?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not add tag';
      setMsg(typeof detail === 'string' ? detail : 'Could not add tag');
      toast.error('Could not add tag');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(tagId) {
    if (!tagId) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.delete(`/properties/${propertyId}/captures/tags/${encodeURIComponent(tagId)}`, {
        params: { session_id: sessionId },
      });
      const next = Array.isArray(res.data?.tags) ? res.data.tags : tags.filter(t => t.id !== tagId);
      setTags(next);
      toast.success('Tag removed');
      onChanged?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not remove tag';
      setMsg(typeof detail === 'string' ? detail : 'Could not remove tag');
      toast.error('Could not remove tag');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="listing-feature-tags" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${p.hair}` }}>
      <Over color={p.ink3}>Feature tags</Over>
      <div style={{ marginTop: 8, fontSize: 12, color: p.ink3, lineHeight: 1.45 }}>
        Amenity markers on the 3D tour. Defaults place at station 0 · yaw 0° · pitch 0° (same Dispatch tags API as GO).
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {AMENITY_PRESETS.map((preset) => {
          const on = label.trim().toLowerCase() === preset.toLowerCase();
          return (
            <button
              key={preset}
              type="button"
              disabled={busy}
              onClick={() => setLabel(preset)}
              style={{
                padding: '5px 9px', fontSize: 12, fontFamily: F.display, cursor: busy ? 'default' : 'pointer',
                borderRadius: 2, border: `1px solid ${on ? T.brick : p.hair}`,
                background: on ? (dark ? 'rgba(210,84,43,0.18)' : '#FBEFE8') : p.surf2,
                color: on ? T.brick : p.ink2,
              }}
            >
              {preset}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ fontSize: 12, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Label
          <input style={inputStyle} value={label} maxLength={80} placeholder="Walk-in closet" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Note (optional)
          <input style={inputStyle} value={note} maxLength={240} placeholder="Cedar lining" onChange={(e) => setNote(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Station
          <select style={inputStyle} value={stationIndex} onChange={(e) => setStationIndex(Number(e.target.value))}>
            {stationOptions.map((idx) => (
              <option key={idx} value={idx}>Station {idx}</option>
            ))}
          </select>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label style={{ fontSize: 12, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
            Yaw
            <input style={inputStyle} type="number" step="1" value={yaw} onChange={(e) => setYaw(e.target.value)} />
          </label>
          <label style={{ fontSize: 12, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
            Pitch
            <input style={inputStyle} type="number" step="1" value={pitch} onChange={(e) => setPitch(e.target.value)} />
          </label>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn variant="primary" dark={dark} disabled={busy || !label.trim()} onClick={handleAdd}>
          {busy ? 'Saving…' : 'Add tag'}
        </Btn>
        {msg ? <span style={{ fontSize: 12, color: msg.startsWith('Tag') ? p.ink2 : T.brick }}>{msg}</span> : null}
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: p.ink3 }}>
        {tags.length ? `${tags.length} tag${tags.length === 1 ? '' : 's'} on this tour` : 'No feature tags yet — none are invented.'}
      </div>
      {tags.length > 0 ? (
        <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tags.map((t) => (
            <li key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', fontSize: 13 }}>
              <span>
                {t.label}{t.note ? ` — ${t.note}` : ''}
                <span style={{ color: p.ink4, fontFamily: F.mono, fontSize: 11 }}>
                  {' '}· station {t.station_index ?? 0} · {Number(t.yaw || 0).toFixed(0)}°
                </span>
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => handleDelete(t.id)}
                style={{
                  border: `1px solid ${p.hair}`, background: 'transparent', color: p.ink3,
                  fontSize: 12, padding: '4px 8px', borderRadius: 2, cursor: busy ? 'default' : 'pointer', fontFamily: F.display,
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}


// ─── Upload/URL dialog ──────────────────────────────────────────────

function AddVisitDialog({ dark, p, propertyId, onClose, onSaved }) {
  const [mode, setMode] = useState("url"); // url | file
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const inputStyle = {
    width: "100%", padding: "10px 12px", fontSize: 14, fontFamily: F.display,
    border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf,
    color: p.ink, outline: "none", boxSizing: "border-box",
  };

  async function handleSave() {
    setSaving(true);
    try {
      if (mode === "url") {
        if (!url.trim()) { toast.error("Enter a Polycam URL"); setSaving(false); return; }
        await api.post(`/properties/${propertyId}/visits/url`, { title: title || "Virtual Visit", embed_url: url.trim() });
      } else {
        if (!file) { toast.error("Select a .sog, .spz, .ply or .splat file"); setSaving(false); return; }
        // Direct-to-Storage upload: no 4.5MB API body cap (whole-home splats are 30–200 MB).
        const { data: sign } = await api.post(`/properties/${propertyId}/visits/sign`, { filename: file.name });
        if (!sign?.signed_url) throw new Error("No upload URL");
        const put = await fetch(sign.signed_url, {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream", "x-upsert": "false" },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed (${put.status})`);
        await api.post(`/properties/${propertyId}/visits/file`, {
          storage_path: sign.storage_path,
          title: title || file.name,
          file_name: file.name,
          file_size: file.size,
        });
      }
      toast.success("Visit added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to save visit");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: p.surf, border: `1px solid ${p.hair}`, borderRadius: 2,
        padding: 32, width: 480, maxWidth: "calc(100vw - 48px)",
        display: "flex", flexDirection: "column", gap: 20,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 18, fontWeight: 500, fontFamily: F.display, color: p.ink }}>Add Virtual Visit</span>
          <div style={{ cursor: "pointer", color: p.ink3 }} onClick={onClose}><I name="x" size={18} color={p.ink3}/></div>
        </div>

        {/* Mode switcher */}
        <div style={{ display: "flex", gap: 8 }}>
          {[{ id: "url", label: "Polycam URL" }, { id: "file", label: "Upload splat file" }].map(opt => (
            <button
              key={opt.id}
              onClick={() => setMode(opt.id)}
              style={{
                flex: 1, padding: "8px 0", fontSize: 13, fontFamily: F.display,
                border: `1px solid ${mode === opt.id ? T.brick : p.hair}`,
                background: mode === opt.id ? T.brickSoft : "transparent",
                color: mode === opt.id ? T.brickDeep : p.ink2,
                borderRadius: 2, cursor: "pointer", fontWeight: mode === opt.id ? 500 : 400,
              }}
            >{opt.label}</button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            style={inputStyle}
            placeholder="Title (optional)"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
          {mode === "url" ? (
            <input
              style={inputStyle}
              placeholder="https://poly.cam/capture/..."
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
          ) : (
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${p.hair}`, borderRadius: 2, padding: "24px 16px",
                textAlign: "center", cursor: "pointer", color: p.ink3, fontSize: 13,
                fontFamily: F.display,
              }}
            >
              {file ? (
                <span style={{ color: p.ink }}>{file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
              ) : (
                <>Click to select a .sog, .spz, .ply or .splat file</>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={VISIT_ACCEPT}
                style={{ display: "none" }}
                onChange={e => setFile(e.target.files?.[0] || null)}
              />
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Btn variant="ghost" dark={dark} onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn variant="primary" dark={dark} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Add Visit"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [visits, setVisits] = useState([]);
  const [activeVisit, setActiveVisit] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [downloading, setDownloading] = useState(false);
  const [zipPreset, setZipPreset] = useState("original");
  const [openingListing, setOpeningListing] = useState(false);
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [captures, setCaptures] = useState(EMPTY_CAPTURES);
  const [tourEmbedKey, setTourEmbedKey] = useState(0);
  const [activity, setActivity] = useState([]);
  const [copyingShare, setCopyingShare] = useState(false);
  const [copyingListing, setCopyingListing] = useState(false);
  const [isClosedLiving, setIsClosedLiving] = useState(false);
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const { data: listingMedia } = useListingMedia({ propertyId: id });
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => { setGalleryExpanded(false); }, [activeTab]);

  useEffect(() => {
    api.get(`/properties/${id}`)
      .then(r => {
        setProperty(r.data);
        const st = (r.data?.status || '').toLowerCase();
        setIsClosedLiving(['completed', 'delivered', 'archived', 'cancelled'].includes(st) || !!r.data?.closed);
        setLoading(false);
      })
      .catch(() => {
        setProperty(null);
        setLoading(false);
        toast.error("Could not load this shoot file");
      });
  }, [id]);

  useEffect(() => {
    api.get(`/properties/${id}/deliverables`)
      .then(r => { setDeliverables(r.data.deliverables || []); setMediaLoading(false); })
      .catch(() => setMediaLoading(false));
  }, [id]);

  function loadVisits() {
    api.get(`/properties/${id}/visits`)
      .then(r => {
        // Phone 3D tours belong on CaptureCard via capture_sessions, not Virtual Visit.
        const t = (r.data.visits || []).filter((v) => {
          const embed = String(v.embed_url || "");
          if (embed.includes("/api/public/tours/")) return false;
          if ((v.title || "") === "3D tour" && v.type === "url") return false;
          return true;
        });
        setVisits(t);
        if (t.length > 0) setActiveVisit((prev) => (prev && t.find((v) => v.id === prev.id)) || t[0]);
      })
      .catch(() => {});
  }

  useEffect(() => { loadVisits(); }, [id]); // eslint-disable-line

  function loadCaptures() {
    api.get(`/properties/${id}/captures`)
      .then(r => setCaptures({ ...EMPTY_CAPTURES, ...(r.data || {}) }))
      .catch(() => setCaptures(EMPTY_CAPTURES));
  }

  useEffect(() => { loadCaptures(); }, [id]); // eslint-disable-line

  useEffect(() => {
    api.get(`/properties/${id}/activity`)
      .then(r => {
        setActivity(r.data?.items || []);
        if (r.data?.closed) setIsClosedLiving(true);
      })
      .catch(() => setActivity([]));
  }, [id]);

  async function createListingFile() {
    if (!property || creatingDeal) return;
    setCreatingDeal(true);
    try {
      const { data } = await api.post("/transactions", {
        deal_type: "sale",
        property_address: property.address || property.property_address || "Address TBD",
        city: property.city || null,
        province: property.province || property.state || null,
        postal_code: property.postal_code || null,
        notes: "Created from shoot file",
        job_id: property.property_id || property.id || id,
      });
      const dealId = data?.id;
      if (!dealId) throw new Error("No listing id returned");
      setProperty(prev => prev ? { ...prev, transaction_id: dealId } : prev);
      toast.success("Listing file created");
      navigate(`/transactions/${dealId}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not create listing file";
      toast.error(typeof msg === "string" ? msg : "Could not create listing file");
    } finally {
      setCreatingDeal(false);
    }
  }

  async function copyClientLink() {
    if (copyingShare) return;
    setCopyingShare(true);
    try {
      const r = await api.get(`/properties/${id}/share`);
      const url = r.data?.share_url || `${window.location.origin}${r.data?.path || ''}`;
      await navigator.clipboard.writeText(url);
      toast.success('Client media link copied');
    } catch {
      toast.error('Could not create client link');
    } finally {
      setCopyingShare(false);
    }
  }

  async function copyListingSiteLink() {
    if (copyingListing) return;
    setCopyingListing(true);
    try {
      const r = await api.get(`/properties/${id}/share`);
      const url = r.data?.listing_url || `${window.location.origin}${r.data?.listing_path || ''}`;
      if (!url) throw new Error('missing listing url');
      await navigator.clipboard.writeText(url);
      toast.success('Listing site link copied');
    } catch {
      toast.error('Could not create listing site link');
    } finally {
      setCopyingListing(false);
    }
  }

  async function openListingSite() {
    if (openingListing) return;
    setOpeningListing(true);
    try {
      const r = await api.get(`/properties/${id}/share`);
      const token = r.data?.token;
      const path = r.data?.listing_path || (token ? `/listing/${token}` : null);
      if (!path) throw new Error("missing token");
      window.open(path, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not open listing site");
    } finally {
      setOpeningListing(false);
    }
  }

  async function deleteVisit(visitId) {
    try {
      await api.delete(`/visits/${visitId}`);
      toast.success("Visit removed");
      const updated = visits.filter(t => t.id !== visitId);
      setVisits(updated);
      setActiveVisit(updated[0] || null);
      loadCaptures();
    } catch {
      toast.error("Failed to remove visit");
    }
  }

  const handleDownloadAll = async () => {
    if (downloading) return;
    if (!id) return;
    const countHint = (deliverables && deliverables.length) || property?.files_count || 0;
    if (!deliverables?.length && !property?.files_count) {
      toast.info("No downloadable files available yet.");
      return;
    }
    setDownloading(true);
    try {
      const res = await api.get(`/properties/${id}/media.zip`, {
        params: { preset: zipPreset || "original" },
        responseType: "blob",
        timeout: 300000,
      });
      const blob = res.data;
      if (blob && blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        let detail = "Failed to prepare zip";
        try { detail = JSON.parse(text).detail || detail; } catch { /* keep */ }
        throw new Error(typeof detail === "string" ? detail : "Failed to prepare zip");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      a.download = match?.[1] || `listing-media-${id}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(countHint ? `Zip ready · ${countHint} files` : "Zip ready");
    } catch (err) {
      let msg = "Failed to prepare zip";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : msg;
        } catch { /* keep */ }
      } else if (typeof err?.message === "string" && err.message) {
        msg = err.message;
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <Loading />;

  if (!property) return (
    <div style={{ padding: 48, textAlign: 'center', color: p.ink3, fontSize: 14 }}>
      Shoot file not found.
      <div style={{ marginTop: 16 }}>
        <Btn variant="ghost" dark={dark} onClick={() => navigate(-1)}>Go back</Btn>
      </div>
    </div>
  );

  const boughtTour = !!captures.purchased?.tour;
  const boughtFloor = !!captures.purchased?.floorplan;
  const boughtVisit = !!captures.purchased?.visit;
  const showCaptureSlots = boughtTour || boughtFloor;
  const showVisitTab = boughtVisit || visits.length > 0;
  const mediaTabs = MEDIA_TABS.filter(tab => tab !== 'Virtual Visit' || showVisitTab);
  const currentTab = (!showVisitTab && activeTab === 'Virtual Visit') ? 'All' : activeTab;

  const counts = mediaTabs.reduce((acc, tab) => {
    if (tab === 'All') acc[tab] = deliverables.length;
    else if (tab === 'Virtual Visit') acc[tab] = visits.length;
    else acc[tab] = deliverables.filter(f => deliverableTab(f) === tab).length;
    return acc;
  }, {});

  const visibleFiles = currentTab === 'All'
    ? deliverables
    : currentTab === 'Virtual Visit'
    ? []
    : deliverables.filter(f => deliverableTab(f) === currentTab);

  const gridFiles = galleryExpanded ? visibleFiles : visibleFiles.slice(0, 12);
  const heroFile = deliverables.find(f => f.thumbnail_url) || null;

  const totalFiles = deliverables.length || property.files_count || 0;

  const completedAt = property.completed_at
    ? new Date(property.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: F.display, fontSize: 13, color: p.ink2, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS, whiteSpace: 'nowrap' };
  const tabLabel = (tab) => (counts[tab] > 0 ? <>{tab === 'Virtual Visit' ? 'Virtual visit' : tab} <span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink4 }}>{counts[tab]}</span></> : (tab === 'Virtual Visit' ? 'Virtual visit' : tab));

  return (
    <div data-testid="property-detail-page" style={{ display: 'flex', flexDirection: 'column', gap: 28, fontFamily: F.display, color: p.ink, minWidth: 0 }}>
      <style>{TAB_CSS}</style>

      {showAddVisit && (
        <AddVisitDialog
          dark={dark} p={p}
          propertyId={id}
          onClose={() => setShowAddVisit(false)}
          onSaved={() => { loadVisits(); loadCaptures(); }}
        />
      )}

      {/* Back */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, color: p.ink3, fontSize: 13, cursor: 'pointer' }}
        onClick={() => navigate('/properties')}
      >
        <I name="arrL" size={14} color={p.ink3}/>
        <span>Media library</span>
      </div>

      {/* Hero */}
      {heroFile?.thumbnail_url
        ? <img src={heroFile.thumbnail_url} alt={property.address || ''} style={{ width: '100%', height: 'clamp(220px, 42vw, 420px)', objectFit: 'cover', borderRadius: 2, display: 'block' }}/>
        : <Img w="100%" h="clamp(160px, 30vw, 300px)" dark={dark}/>
      }

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]" style={{ gap: 48 }}>

        {/* Left: Media grid */}
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, lineHeight: 1.15, margin: 0, overflowWrap: 'anywhere' }}>{property.address}</h1>
              <div style={{ fontSize: 13.5, color: p.ink3, marginTop: 6 }}>
                {[property.package || null, `${totalFiles} ${totalFiles === 1 ? 'file' : 'files'}`, isClosedLiving ? 'Closed, living file' : null].filter(Boolean).join(' · ')}
              </div>
              {formatShootWhen(property) ? (
                <div data-testid="property-shoot-window" style={{ fontSize: 13, color: p.ink3, marginTop: 4 }}>
                  Shoot {formatShootWhen(property)}
                </div>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <BookMediaButton propertyId={id} summary={listingMedia} dark={dark} />
              {currentTab === 'Virtual Visit' ? (
                <Btn variant="ghost" dark={dark} icon="plus" onClick={() => setShowAddVisit(true)}>Add visit</Btn>
              ) : (
                <Btn variant="ghost" dark={dark} icon="dl" onClick={handleDownloadAll} disabled={downloading}>
                  {downloading ? 'Preparing zip…' : `Download all · ${totalFiles}`}
                </Btn>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px 18px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" style={linkBtn} onClick={copyClientLink} disabled={copyingShare}>{copyingShare ? 'Copying…' : 'Copy client media'}</button>
              <button type="button" style={linkBtn} onClick={copyListingSiteLink} disabled={copyingListing}>{copyingListing ? 'Copying…' : 'Copy listing site'}</button>
              <button type="button" style={linkBtn} onClick={openListingSite} disabled={openingListing}>{openingListing ? 'Opening…' : 'Open listing site'}</button>
            </div>
            {currentTab !== 'Virtual Visit' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: p.ink3, flexWrap: 'wrap' }}>
                <span>Download size</span>
                <select
                  data-testid="property-zip-preset"
                  value={zipPreset}
                  onChange={(e) => setZipPreset(e.target.value)}
                  disabled={downloading}
                  style={{ fontFamily: F.display, fontSize: 13, padding: '6px 10px', borderRadius: 2, border: `1px solid ${p.hairS}`, background: p.bg, color: p.ink }}
                >
                  <option value="original">As delivered (2400px)</option>
                  <option value="mls">MLS (2400px)</option>
                  <option value="web">Web (1920px)</option>
                </select>
                <span style={{ color: p.ink4 }}>
                  {zipPreset === 'mls' ? '4:3 crop, 2400px long edge.' : zipPreset === 'web' ? '1920px long edge. Lighter files.' : 'Edited photos at 2400px. Video and plans as delivered.'}
                </span>
              </label>
            )}
          </div>

          <ListingMediaPrompt propertyId={id} summary={listingMedia} dark={dark} p={p} style={{ marginBottom: 24 }} onOpenShoot={(pid) => navigate(`/properties/${pid}`)} />

          {/* Activity / History — CoStar lesson: own the record on the file */}
          {(activity.length > 0 || isClosedLiving) && (
            <div data-testid="living-file-activity" style={{ marginBottom: 28 }}>
              <Section title="Activity">
                {activity.length === 0 ? (
                  <div style={{ fontSize: 13, color: p.ink3 }}>Living file. Media stays on the desk after close.</div>
                ) : (
                  <List>
                    {activity.map((item, i) => (
                      <Row key={i} title={item.label}
                        status={<span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink3 }}>{item.at ? new Date(item.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>} />
                    ))}
                  </List>
                )}
              </Section>
            </div>
          )}

          {/* Media / Capture — purchased 3D tour / floor plan only. Virtual visit stays on VisitViewer. */}
          {showCaptureSlots ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>Capture</div>
              <div data-testid="listing-capture" style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {boughtTour ? (
                  <CaptureCard
                    title="3D tour"
                    emptyLabel="No 3D tour capture yet"
                    block={captures.splat}
                    dark={dark}
                    p={p}
                  >
                    {tourEmbedSrc(captures.splat?.embed_url) ? (
                      <iframe
                        key={tourEmbedKey}
                        src={tourEmbedSrc(captures.splat.embed_url)}
                        title="3D tour"
                        allow="fullscreen; xr-spatial-tracking"
                        style={{ marginTop: 12, width: '100%', height: 280, border: 0, borderRadius: 2, background: '#0a0a0a', display: 'block' }}
                      />
                    ) : null}
                    <FeatureTagsPanel
                      propertyId={id}
                      block={captures.splat}
                      dark={dark}
                      p={p}
                      onChanged={() => {
                        setTourEmbedKey((k) => k + 1);
                        loadCaptures();
                      }}
                    />
                  </CaptureCard>
                ) : null}
                {boughtFloor ? (
                  <CaptureCard
                    title="Floor plan"
                    emptyLabel="No floor plan capture yet"
                    block={captures.floorplan}
                    dark={dark}
                    p={p}
                  >
                    {captures.floorplan?.output_image_url ? (
                      <img
                        src={captures.floorplan.output_image_url}
                        alt="Floor plan"
                        style={{ marginTop: 12, width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 2, display: 'block', background: p.surf2 }}
                      />
                    ) : null}
                  </CaptureCard>
                ) : null}
              </div>
            </>
          ) : null}

          <div className="pd-tabs" style={{ marginBottom: 24 }}><StatusTabs label="Media" testid="pd-tabs" options={mediaTabs.map(t => ({ key: t, label: tabLabel(t) }))} value={currentTab} onChange={setActiveTab} /></div>
          <div className="pd-tabsel" style={{ marginBottom: 20 }}><SmallSelect label="Media" value={currentTab} onChange={setActiveTab} options={mediaTabs.map(t => ({ key: t, label: `${t === 'Virtual Visit' ? 'Virtual visit' : t}${counts[t] > 0 ? ` (${counts[t]})` : ''}` }))} /></div>

          {/* Virtual Visit panel */}
          {currentTab === 'Virtual Visit' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {visits.length === 0 ? (
                <Empty action={<Btn variant="ghost" dark={dark} icon="plus" onClick={() => setShowAddVisit(true)}>Add virtual visit</Btn>}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, marginBottom: 6 }}>No virtual visits yet</div>
                  <div style={{ fontSize: 13, color: p.ink3, maxWidth: 420 }}>
                    Add a Polycam share link, which opens on Polycam, or upload a Gaussian splat (.sog, .spz, .ply or .splat) for a hosted Visit you can embed.
                  </div>
                </Empty>
              ) : (
                <>
                  {/* Visit selector if multiple */}
                  {visits.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {visits.map(t => (
                        <button
                          key={t.id}
                          onClick={() => setActiveVisit(t)}
                          style={{
                            padding: '6px 14px', fontSize: 12, fontFamily: F.display, borderRadius: 2,
                            border: `1px solid ${activeVisit?.id === t.id ? p.ink : p.hair}`,
                            background: 'transparent',
                            color: activeVisit?.id === t.id ? p.ink : p.ink2,
                            cursor: 'pointer',
                          }}
                        >
                          {t.title || `Visit ${visits.indexOf(t) + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Active visit viewer */}
                  {activeVisit && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontSize: 15, fontWeight: 500, color: p.ink }}>
                          {activeVisit.title || 'Virtual visit'}
                        </span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span
                            data-testid="visit-kind-label"
                            style={{ fontSize: 12.5, color: p.ink3 }}>
                            {visitKindLabel(activeVisit)}
                          </span>
                          <div
                            style={{ cursor: 'pointer', color: p.ink4 }}
                            onClick={() => deleteVisit(activeVisit.id)}
                          >
                            <I name="trash" size={14} color={p.ink4}/>
                          </div>
                        </div>
                      </div>
                      <VisitViewer visit={activeVisit} desk/>
                      <VisitEmbedPanel visit={activeVisit} p={p} onChanged={loadVisits}/>
                      {activeVisit.type === 'url' && activeVisit.embed_url && (
                        <div style={{ marginTop: 8, fontSize: 12, color: p.ink4, wordBreak: 'break-all' }}>
                          {activeVisit.embed_url}
                        </div>
                      )}
                      {activeVisit.type === 'file' && activeVisit.file_name && (
                        <div style={{ marginTop: 8, fontSize: 12, color: p.ink4 }}>
                          {activeVisit.file_name}
                          {activeVisit.file_size && ` · ${(activeVisit.file_size / 1024 / 1024).toFixed(1)} MB`}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {currentTab === 'Plan' && boughtFloor && captures.floorplan?.output_image_url && (
                <img
                  src={captures.floorplan.output_image_url}
                  alt="Floor plan"
                  style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 2, display: 'block', background: p.surf2, marginBottom: 16 }}
                />
              )}
              {/* Thumbnail grid */}
              {mediaLoading ? (
                <div className="pd-grid">
                  {Array.from({ length: 8 }).map((_, i) => <Img key={i} h={140} r={2} dark={dark}/>)}
                </div>
              ) : gridFiles.length === 0 ? (
                currentTab === 'Plan' && boughtFloor && captures.floorplan?.output_image_url ? null : (
                <Empty>{currentTab === 'All' ? 'No files yet. Media arrives after the shoot.' : `No ${currentTab.toLowerCase()} files yet.`}</Empty>
                )
              ) : (
                <div className="pd-grid">
                  {gridFiles.map((file, i) => {
                    const qc = String(file.qc_status || '').trim().toLowerCase();
                    const qcPill = qc === 'rejected'
                      ? { tone: 'brick', label: 'Held' }
                      : qc === 'flagged'
                        ? { tone: 'warn', label: 'Review' }
                        : null;
                    return (
                    <div key={file.id || i} style={{ position: 'relative' }}>
                      {file.thumbnail_url
                        ? <img src={file.thumbnail_url} alt={file.filename || ''} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 2, display: 'block' }}/>
                        : <Img h={140} r={2} dark={dark}/>
                      }
                      {qcPill && (
                        <div style={{ position: 'absolute', top: 8, left: 8 }}>
                          <Pill tone={qcPill.tone} dark={dark}>{qcPill.label}</Pill>
                        </div>
                      )}
                      {file.download_url && (
                        <a
                          href={file.download_url}
                          download={file.filename}
                          rel="noopener noreferrer"
                          style={{ position: 'absolute', bottom: 8, right: 8, width: 24, height: 24, background: 'rgba(0,0,0,0.45)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <I name="dl" size={13} color="#fff"/>
                        </a>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {visibleFiles.length > 12 && (
                <div style={{ marginTop: 20 }}>
                  <span style={{ fontSize: 13, color: p.ink3 }}>
                    {galleryExpanded
                      ? `Showing all ${visibleFiles.length} files · `
                      : `${visibleFiles.length - 12} more files · `}
                    <span
                      onClick={() => setGalleryExpanded(v => !v)}
                      style={{ ...linkBtn, display: 'inline' }}
                    >
                      {galleryExpanded ? 'Show less' : 'View all'}
                    </span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

          <Section title="Booking">
            <List>
              <Row title="Agent" status={<span style={{ color: p.ink2 }}>{property.agent_name || 'Not set'}</span>} />
              <Row title="Package" status={<span style={{ color: p.ink2 }}>{property.package || 'Not set'}</span>} />
              {property.vendor_name ? <Row title="Vendor" status={<span style={{ color: p.ink2 }}>{property.vendor_name}</span>} /> : null}
              {formatShootWhen(property) ? (
                <Row title="Shoot" status={<span data-testid="property-arrive-line" style={{ color: p.ink2, textAlign: 'right' }}>{formatShootWhen(property)}</span>} />
              ) : null}
              {visits.length > 0 ? <Row title="Virtual visits" status={<span style={{ fontFamily: F.mono, fontSize: 13, color: p.ink2 }}>{visits.length}</span>} /> : null}
              <Row title="Status" status={<StatusText>{isClosedLiving ? 'Closed, living file' : humanize(property.status || 'unknown')}</StatusText>} />
            </List>
            {completedAt && (
              <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 10 }}>
                Final files released to the agent on {completedAt}. Media stays on this file.
              </div>
            )}
          </Section>

          <Section title="Listing file">
            {property.transaction_id ? (
              <List>
                <Row onClick={() => navigate(`/transactions/${property.transaction_id}`)} title="Open listing file"
                  meta="Contacts, documents, calendar and e-sign live on the deal." />
              </List>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: p.ink3, marginBottom: 14 }}>
                  Create a listing file to attach contacts, documents, calendar events and e-sign to this shoot.
                </div>
                <Btn variant="ghost" dark={dark} onClick={createListingFile} disabled={creatingDeal}>
                  {creatingDeal ? 'Creating…' : 'Create listing file'}
                </Btn>
              </div>
            )}
          </Section>

          <Section title="Deliverables">
            <List>
              {((property.deliverables || []).length > 0
                ? property.deliverables.map(d => typeof d === 'string' ? d : d.name || d.type || JSON.stringify(d))
                : ['High-res photography', '4K drone cinematography', 'Vertical social reel', 'Schematic floor plan', 'MLS-optimized web gallery']
              ).map(item => (
                <Row key={item} title={<span style={{ fontSize: 13.5, fontWeight: 400, color: p.ink2 }}>{item}</span>} status="Included" />
              ))}
              {(property.add_ons || []).map(ao => (
                <Row key={ao.service} title={<span style={{ fontSize: 13.5, fontWeight: 400, color: p.ink2 }}>{ao.service}</span>}
                  status={<span style={{ fontFamily: F.mono, fontSize: 12, color: p.ink3 }}>${ao.price}</span>} />
              ))}
            </List>
          </Section>

        </div>
      </div>
    </div>
  );
}
